import { Divider } from "@dashboard/components/Divider";
import { ProductFragment, useProductVariantChannelListingUpdateMutation } from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { Box, Checkbox, Text } from "@saleor/macaw-ui-next";
import { useIntl } from "react-intl";

import { buildAvailabilityInput } from "./buildAvailabilityInput";
import { productVariantAvailabilityCardMessages as messages } from "./messages";

type Variant = NonNullable<ProductFragment["variants"]>[number];

interface ChannelVariantPurchasabilityProps {
  product: ProductFragment | undefined;
  channelId: string;
  disabled?: boolean;
}

/**
 * Per-variant "available for purchase" controls for a single channel, rendered
 * nested inside that channel's availability block. Each toggle saves on its own
 * via productVariantChannelListingUpdate; Apollo updates the cache from the
 * returned listing so the checkbox reflects the saved value.
 */
export const ChannelVariantPurchasability = ({
  product,
  channelId,
  disabled,
}: ChannelVariantPurchasabilityProps) => {
  const intl = useIntl();
  const notify = useNotifier();
  const [updateListing, updateListingOpts] = useProductVariantChannelListingUpdateMutation({
    onCompleted: data => {
      const errors = data.productVariantChannelListingUpdate?.errors ?? [];

      if (errors.length) {
        errors.forEach(error => notify({ status: "error", text: error.message ?? "" }));

        return;
      }

      notify({ status: "success", text: intl.formatMessage(messages.updated) });
    },
  });

  const listedVariants = (product?.variants ?? []).filter(variant =>
    variant.channelListings?.some(l => l.channel.id === channelId && l.price != null),
  );

  if (listedVariants.length === 0) {
    return null;
  }

  const handleToggle = (variant: Variant, next: boolean) => {
    const variables = buildAvailabilityInput(variant, channelId, next);

    if (variables) {
      updateListing({ variables });
    }
  };

  return (
    <>
      <Divider />
      <Box display="flex" flexDirection="column" gap={2}>
        <Text size={3} fontWeight="bold">
          {intl.formatMessage(messages.title)}
        </Text>
        <Text size={2} color="default2">
          {intl.formatMessage(messages.subtitle)}
        </Text>
        {listedVariants.map(variant => {
          const listing = variant.channelListings?.find(l => l.channel.id === channelId);

          return (
            <Box
              key={variant.id}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={3}
            >
              <Text size={3}>{variant.name || variant.sku}</Text>
              <Checkbox
                checked={!!listing?.isAvailableForPurchase}
                disabled={disabled || updateListingOpts.loading}
                onCheckedChange={checked => handleToggle(variant, checked === true)}
                data-test-id={`variant-purchasable-${variant.id}-${channelId}`}
              >
                <Text size={2} color="default2">
                  {intl.formatMessage(messages.availableForPurchase)}
                </Text>
              </Checkbox>
            </Box>
          );
        })}
      </Box>
    </>
  );
};
